import { GenerativeConstraints } from '../../types';
import { ComposerSession } from './ComposerSession';
import { ComposerOrchestrator } from './ComposerOrchestrator';
import { HarmonyUtils } from '../../utils/music/HarmonyUtils';

/**
 * ComposerEngine - Creates beautiful, UNIQUE music from touches.
 * 
 * FACADE:
 * - Coordinates ComposerSession (State)
 * - Coordinates ComposerOrchestrator (Logic)
 * - Provides clean public API
 */
class ComposerEngineClass {
    private session: ComposerSession;
    private orchestrator: ComposerOrchestrator;
    private listeners: (() => void)[] = [];

    constructor() {
        this.session = new ComposerSession();
        this.orchestrator = new ComposerOrchestrator();
    }

    /**
     * Subscribe to session changes (Key, Scale, Vibe updates).
     * Returns unsubscribe function.
     */
    subscribe(listener: () => void): () => void {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }

    private notifyListeners() {
        this.listeners.forEach(l => l());
    }

    /**
     * Initialize a new random session.
     * Call this when user enters Composer mode.
     */
    initialize(forcePiano: boolean = false): void {
        this.session.initialize(forcePiano);
        this.orchestrator.reset(this.session);
        this.notifyListeners();
    }

    getSuggestedInstrument() {
        return this.session.suggestedInstrument;
    }

    getConstraints(): GenerativeConstraints {
        if (!this.session.isInitialized) {
            this.initialize(false);
        }
        const currentSection = this.orchestrator.getCurrentSection();
        return this.session.getConstraints(currentSection);
    }

    getNotesForTouch(voiceIndex: number, tuning: number = 440, dynamics?: { velocity: number; yPos: number }): Array<{
        freq: number,
        delay: number,
        duration?: number,
        velocity?: number,
        timbre?: { filter: number, release: number, reverb: number },
        dampAll?: boolean
    }> {
        if (!this.session.isInitialized) {
            this.initialize();
        }

        // Capture state before processing
        const prevKeyOffset = this.session.currentKeyOffset;
        let events: any[] = [];

        try {
            events = this.orchestrator.getNotesForTouch(this.session, voiceIndex, tuning, dynamics);
        } catch (e) {
            console.error('[ComposerEngine] Critical Error in getNotesForTouch:', e);
            // Return silent/safe fallback to keep UI alive
            return [];
        }

        // Check for modulation (Real-Time UI Update)
        // Also update on every touch for progress bar smoothness
        const currentProgress = this.orchestrator.getSongProgress();
        if (this.session.currentKeyOffset !== prevKeyOffset || currentProgress > 0) {
            this.notifyListeners();
        }

        return events;
    }

    /**
     * GHOST HAND (Feature 1): Get notes for idle/background comping.
     * Called by ToneAudioService's ghost loop.
     */
    getGhostEvent(tuning: number = 440): Array<{
        freq: number,
        delay: number,
        duration?: number,
        velocity?: number,
        timbre?: { filter: number, release: number, reverb: number },
        dampAll?: boolean
    }> {
        if (!this.session.isInitialized) {
            return [];
        }
        return this.orchestrator.getGhostEvent(this.session, tuning);
    }

    getSessionInfo(): { key: string; scale: string; progression: string; vibeName: string; instrument: string; songProgress: number; structure: string } {
        return {
            key: HarmonyUtils.transposeNote(this.session.rootNote, this.session.currentKeyOffset),
            scale: this.session.scale,
            progression: this.session.progression.name,
            vibeName: this.session.vibeName,
            instrument: this.session.suggestedInstrument,
            songProgress: this.orchestrator.getSongProgress(), // Expose progress
            structure: this.session.structureDefinition.name
        };
    }

    getTouchCount(): number {
        return this.session.touchCount;
    }
}

export const ComposerEngine = new ComposerEngineClass();
